import React, { useId } from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../i18n/TModules';
import { setOrbitPendingWorkspace, useOrbitPendingWorkspaces } from '../orbitPendingWorkspace';
import OrbitChatMenu from '../page/components/OrbitChatMenu';
import { orbitTokens } from '../styles';
import useOrbitChatActions from '../useOrbitChatActions';
import useOrbitChatDelete from '../useOrbitChatDelete';
import useOrbitOpenChat from '../useOrbitOpenChat';

// Figma 6654:2155: 72 tall under the header, a 0.5 rule on every side and no fill. The title sits
// 19.5 in and the 24 "…" 20 from the right edge, both centred on the bar.
const Bar = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  box-sizing: border-box;
  min-height: 72px;
  padding: 0 20px 0 19.5px;
  border: 0.5px solid ${orbitTokens.border};
`;

// The page's chat title at panel scale: Inter Medium 16 on a 28 box.
const Title = styled.h3`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: ${orbitTokens.text};
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuSlot = styled.div`
  display: flex;
  flex: none;
  margin-left: 16px;
`;

export interface IOrbitChatNameBarProps {
  /** Runs once the open chat is deleted; the bar goes with it, so focus needs somewhere to land. */
  onDeleted: () => void;
}

/** The side panel's fixed chat-name bar: the open chat's title and its "…" menu. */
const OrbitChatNameBar: React.FC<IOrbitChatNameBarProps> = ({ onDeleted }) => {
  const { t } = useMultiLanguageTranslation();
  const { sessionId, record, title: chatTitle } = useOrbitOpenChat();
  const pending = useOrbitPendingWorkspaces();
  const actions = useOrbitChatActions();
  const confirmDelete = useOrbitChatDelete();
  const titleId = useId();
  const title = chatTitle ?? t(`${TOrbit}.page.newChat`);
  const workspace = (sessionId === null ? undefined : pending[sessionId]) ?? record?.workspace;

  return (
    <Bar data-testid="orbit-chat-name-bar">
      <Title id={titleId} data-testid="orbit-chat-name">
        {title}
      </Title>
      <MenuSlot>
        {/* A first question still waiting for its session has nothing to delete, or to file. */}
        <OrbitChatMenu
          anchor="panel"
          describedBy={titleId}
          onRename={record !== undefined ? () => actions.rename(record) : null}
          pin={
            record !== undefined
              ? { pinned: record.pinned, onToggle: () => actions.setPinned(record, !record.pinned) }
              : null
          }
          onDelete={sessionId !== null ? () => confirmDelete(sessionId, title, onDeleted) : null}
          workspace={
            sessionId !== null
              ? {
                  current: workspace ?? null,
                  onChoose: tag => {
                    if (record !== undefined) {
                      actions.file(record, tag);
                    } else {
                      setOrbitPendingWorkspace(sessionId, tag);
                    }
                  }
                }
              : null
          }
          testId="orbit-panel-chat-menu"
        />
      </MenuSlot>
    </Bar>
  );
};

export default OrbitChatNameBar;
